import FormInput from '@c/forms/controls/FormInput';
import FormLabel from '@c/forms/controls/FormLabel';
import Toggle from '@ui/Toggle';
import { CategoryDocument } from 'models/category';
import React from 'react';

const ShippingLabel = ({
  measurementSection,
  errors,
  form,
  register,
  handleWeightChange,
  toggleShipInsurance,
  insuranceEnabled,
  hideInsuranceOption,
  total,
  selectedCat,
  setError,
  clearErrors,
}: {
  measurementSection?: React.ReactElement;
  errors: any;
  form: any;
  register: any;
  handleWeightChange: (weight: number) => void;
  toggleShipInsurance: () => void;
  insuranceEnabled?: boolean;
  hideInsuranceOption?: boolean;
  total?: number;
  selectedCat?: CategoryDocument;
  setError?: any;
  clearErrors?: any;
}) => {
  const [weight, setWeight] = React.useState({
    pounds: 0,
    ounces: 0,
  });
  return (
    <>
      <div className="mt-8 hidden sm:block">
        <h5 className="text-[1.8rem] font-semibold text-brand-lightest-black">
          Package Details
        </h5>
      </div>

      <p className="mb-8 mt-4 w-full text-[1.5rem] text-black">
        Enter the dimensions of the package you will be shipping. Be accurate -
        these will be used to create a shipping label.
      </p>

      <div className="flex w-full flex-col gap-[1.6rem] lg:flex-row">
        {measurementSection && (
          <div className="flex lg:w-1/2">
            <div className="flex h-fit w-full flex-col overflow-clip rounded-[.8rem] border-2 border-brand-lightest-gray sm:mt-[2.4rem]">
              {/* measurements guide */}
              <div className="p-[.8rem] ">
                <h5 className="text-[1.6rem] font-semibold">
                  Guide Measurements
                </h5>

                <p className="mt-2 text-[1.6rem] text-brand-dark-gray">
                  Click to use its measurements
                </p>
              </div>

              {measurementSection}
            </div>
          </div>
        )}

        <div className="flex flex-col gap-[1.6rem] lg:w-1/2">
          {/* Package Details */}
          <div className="sm:gap-[2.4rem flex h-fit w-full flex-col gap-[1.6rem]">
            {/* weight */}
            {errors?.shipment?.package?.weight?.value && (
              <p className="text-[1.3rem] font-semibold text-brand-red">
                {errors?.shipment?.package?.weight.value.message}
              </p>
            )}
            {errors?.shipment?.package?.weight && (
              <p className="text-[1.3rem] font-semibold text-brand-red">
                {errors?.shipment?.package?.weight.message}
              </p>
            )}

            <FormLabel value="Weight" required>
              <div className="flex gap-[1.6rem]">
                <FormInput
                  type="number"
                  placeholder="Pounds (*)"
                  unit="lb"
                  value={
                    Math.floor(
                      (form.shipment?.package?.weight?.value ?? 0) / 16
                    ) || ''
                  }
                  onChange={(e) => {
                    const pounds = parseInt(e.target.value) || 0;
                    const newWeight = {
                      ...weight,
                      pounds: pounds,
                    };
                    setWeight(newWeight);
                    const totalWeight = pounds * 16 + weight.ounces;
                    handleWeightChange(totalWeight);
                  }}
                />

                <FormInput
                  type="number"
                  placeholder="Ounces (*)"
                  unit="oz"
                  value={(form.shipment?.package?.weight?.value ?? 0) % 16}
                  onChange={(e) => {
                    const ounces = parseInt(e.target.value) || 0;
                    const newWeight = {
                      ...weight,
                      ounces: ounces,
                    };
                    setWeight(newWeight);
                    const totalWeight = weight.pounds * 16 + ounces;
                    handleWeightChange(totalWeight);
                  }}
                />
              </div>
            </FormLabel>

            {/* length */}
            <div>
              {errors?.shipment?.package?.dimensions?.length && (
                <p className="text-[1.3rem] font-semibold text-brand-red">
                  {errors.shipment.package.dimensions.length.message}
                </p>
              )}
              {errors?.shipment?.package?.dimensions?.width && (
                <p className="text-[1.3rem] font-semibold text-brand-red">
                  {errors.shipment.package.dimensions.width.message}
                </p>
              )}
              {errors?.shipment?.package?.dimensions?.height && (
                <p className="text-[1.3rem] font-semibold text-brand-red">
                  {errors.shipment.package.dimensions.height.message}
                </p>
              )}
            </div>

            <div className="flex flex-row gap-[1.6rem]">
              <FormLabel value="Length" required>
                <FormInput
                  type="number"
                  {...register('shipment.package.dimensions.length', {
                    valueAsNumber: true,
                    required: true,
                    onBlur: (e: any) => {
                      if (
                        selectedCat?.pkg_ranges?.length?.min &&
                        e.target.value < selectedCat?.pkg_ranges?.length?.min
                      ) {
                        setError?.('shipment.package.dimensions.length', {
                          message: `Length must be at least ${selectedCat?.pkg_ranges?.length?.min} inches`,
                        });
                      } else if (
                        selectedCat?.pkg_ranges?.length?.max &&
                        e.target.value > selectedCat?.pkg_ranges?.length?.max
                      ) {
                        setError?.('shipment.package.dimensions.length', {
                          message: `Length must be at most ${selectedCat?.pkg_ranges?.length?.max} inches`,
                        });
                      } else {
                        clearErrors?.('shipment.package.dimensions.length');
                      }
                    },
                  })}
                  error={!!errors?.shipment?.package?.dimensions?.length}
                  unit={'in'}
                />
              </FormLabel>

              <FormLabel value="Width" required>
                <FormInput
                  type="number"
                  {...register('shipment.package.dimensions.width', {
                    valueAsNumber: true,
                    required: true,
                    onBlur: (e: any) => {
                      if (
                        selectedCat?.pkg_ranges?.width?.min &&
                        e.target.value < selectedCat?.pkg_ranges?.width?.min
                      ) {
                        setError?.('shipment.package.dimensions.width', {
                          message: `Width must be at least ${selectedCat?.pkg_ranges?.width?.min} inches`,
                        });
                      } else if (
                        selectedCat?.pkg_ranges?.width?.max &&
                        e.target.value > selectedCat?.pkg_ranges?.width?.max
                      ) {
                        setError?.('shipment.package.dimensions.width', {
                          message: `Width must be at most ${selectedCat?.pkg_ranges?.width?.max} inches`,
                        });
                      } else {
                        clearErrors?.('shipment.package.dimensions.width');
                      }
                    },
                  })}
                  error={!!errors?.shipment?.package?.dimensions?.width}
                  unit={'in'}
                />
              </FormLabel>

              <FormLabel value="Height" required>
                <FormInput
                  type="number"
                  {...register('shipment.package.dimensions.height', {
                    valueAsNumber: true,
                    required: true,
                    onBlur: (e: any) => {
                      if (
                        selectedCat?.pkg_ranges?.height?.min &&
                        e.target.value < selectedCat?.pkg_ranges?.height?.min
                      ) {
                        setError?.('shipment.package.dimensions.height', {
                          message: `Height must be at least ${selectedCat?.pkg_ranges?.height?.min} inches`,
                        });
                      } else if (
                        selectedCat?.pkg_ranges?.height?.max &&
                        e.target.value > selectedCat?.pkg_ranges?.height?.max
                      ) {
                        setError?.('shipment.package.dimensions.height', {
                          message: `Height must be at most ${selectedCat?.pkg_ranges?.height?.max} inches`,
                        });
                      } else {
                        clearErrors?.('shipment.package.dimensions.height');
                      }
                    },
                  })}
                  error={!!errors?.shipment?.package?.dimensions?.height}
                  unit={'in'}
                />
              </FormLabel>
            </div>
          </div>
        </div>
      </div>

      {!hideInsuranceOption && (form.price === undefined || form.price > 0) && (
        <div className="mt-16">
          <div className="flex items-center gap-x-4">
            <span
              className={`text-[1.8rem] ${
                insuranceEnabled ? 'text-brand-secondary' : 'text-black'
              }`}
            >
              {(form.shipment?.package?.insured_value?.amount ?? 0) > 0 ||
              !!insuranceEnabled ||
              form.price
                ? `Add Shipping Insurance for $${(
                    (form.price ?? total ?? 0) * 0.04
                  ).toFixed(2)}`
                : 'Add Shipping Insurance'}
            </span>

            <Toggle
              onChange={() => toggleShipInsurance()}
              enabled={
                (form.shipment?.package?.insured_value?.amount ?? 0) > 0 ||
                !!insuranceEnabled
              }
            />
          </div>

          <div className="mt-8">
            <h3 className="text-[1.8rem] text-zinc-600">
              <span className="text-brand-secondary">
                Guarantee your payout if the package is lost or damaged
              </span>
              . We suggest insuring your package through MX Locker Shipping
              Insurance, which costs 4% of the listed item price. This cost is
              deferred until the item is sold, and it will be deducted from your
              earnings. If you choose not to opt in, you will be responsible for
              addressing any shipping and handling issues that may arise.
            </h3>
          </div>
        </div>
      )}
    </>
  );
};

export default ShippingLabel;
